import React from 'react';

import { Button } from '@crazyegginc/hatch';

import { useMetric, useAuthContext, useModal, usePermissions, useManageSubscriptionPath } from '/src/hooks';
import { SupportLinks } from '/src/support';
import { WarningItem } from './WarningItem';
import { EnterprisePlanModal } from '/src/features/account/components/modals/EnterprisePlanModal';
import { FEATURES, CAPABILITY_REASONS } from '/src/features/_global/constants';
import { AppLink } from '/src/features/_global/components/AppLink';
import { SupportLink } from '/src/features/_global/components/SupportLink';

const DEFAULT_INSTALL_PATH = '/install';

export function hasRenderableWarning(warnings = [], { isImpersonated = false } = {}) {
  return warnings.some((warning) => {
    switch (warning.name) {
      case 'FULL_CHECK_NOT_COMPLETED':
      case 'DUPLICATE_TRACKING_SCRIPT_INSTALLED':
      case 'OUTDATED_TRACKING_SCRIPT_INSTALLED':
      case 'CACHED_VERSION_OF_TRACKING_SCRIPT':
      case 'TRACKING_SCRIPT_IN_NON_OPTIMAL_LOCATION':
      case 'TRACK_BY_NAME_CODE_DETECTED':
      case 'PROTOTYPE_JS_DEFINED':
      case 'MONTHLY_VISITS_QUOTA_REACHED':
      case 'TOTAL_SNAPSHOT_QUOTA_REACHED':
      case 'MONTHLY_RECORDINGS_QUOTA_REACHED':
        return true;
      case 'NATIVE_FUNCTIONS_OVERRIDEN':
        return isImpersonated;
      default:
        return false;
    }
  });
}

export function RenderWarnings({ warnings, finalUrl, installPath = DEFAULT_INSTALL_PATH, failedCookies, ui = {} }) {
  const trackMetric = useMetric();
  const permissions = usePermissions();
  const modal = useModal();
  const subscriptionPath = useManageSubscriptionPath();

  const canChangePlan = permissions.can('changePlan', FEATURES.BILLING);
  const showEnterpriseModal = !canChangePlan?.allowed && canChangePlan?.reason === CAPABILITY_REASONS.CONTACT_SUPPORT;

  const { sessionInfo } = useAuthContext();
  const isImpersonated = sessionInfo?.isImpersonated ?? false;
  const shouldRenderWarnings = hasRenderableWarning(warnings, { isImpersonated });

  if (!shouldRenderWarnings && !failedCookies.length) {
    return null;
  }

  return (
    <>
      <div className={ui.spacerClassName ?? 'h-[30px]'} />
      {failedCookies.length > 0 ? (
        <WarningItem
          header="Cookies blocked"
          iconClassName={ui.iconBackgroundClassName}
          iconGlyphClassName={ui.statusIconClassName}
          iconContainerClassName={ui.statusIconContainerClassName}
          itemClassName={ui.statusItemClassName}
          strongClassName={ui.strongClassName}
          description={
            <div>
              Your site is preventing Crazy Egg from setting the following cookies:
              <ul className="ml-5 list-outside list-disc font-mono my-2">
                {failedCookies.map((c) => (
                  <li key={c.name} className="font-mono">
                    {c.name}
                  </li>
                ))}
              </ul>
              Please update any cookie-blocking software on your site to allow these cookies to be set. Otherwise Crazy
              Egg may not function properly.
            </div>
          }
        />
      ) : null}
      {warnings.find((warning) => warning.name === 'FULL_CHECK_NOT_COMPLETED') && (
        <WarningItem
          header="Partial Script Check Failed"
          iconClassName={ui.iconBackgroundClassName}
          iconGlyphClassName={ui.statusIconClassName}
          iconContainerClassName={ui.statusIconContainerClassName}
          itemClassName={ui.statusItemClassName}
          strongClassName={ui.strongClassName}
          description="The script is installed but we had a problem running the full check. If data is not being tracked, please contact support."
          ctaButton={
            <Button component={SupportLink} href={SupportLinks.general.newSupportRequest}>
              Contact support
            </Button>
          }
        />
      )}
      {warnings.find((warning) => warning.name === 'DUPLICATE_TRACKING_SCRIPT_INSTALLED') && (
        <WarningItem
          header="Duplicate Tracking Script Installed"
          iconClassName={ui.iconBackgroundClassName}
          iconGlyphClassName={ui.statusIconClassName}
          iconContainerClassName={ui.statusIconContainerClassName}
          itemClassName={ui.statusItemClassName}
          strongClassName={ui.strongClassName}
          description="It looks like you have multiple tracking scripts installed."
          howtoFix={
            <>
              Remove the duplicate tracking scripts. Then re-check its installation.
              <br />
              Visit this{' '}
              <SupportLink className="text-link" href={SupportLinks.scriptCheck.duplicateScript}>
                help article
              </SupportLink>{' '}
              to learn more.
            </>
          }
        />
      )}
      {warnings.find((warning) => warning.name === 'OUTDATED_TRACKING_SCRIPT_INSTALLED') && (
        <WarningItem
          header="Outdated Tracking Script Installed"
          iconClassName={ui.iconBackgroundClassName}
          iconGlyphClassName={ui.statusIconClassName}
          iconContainerClassName={ui.statusIconContainerClassName}
          itemClassName={ui.statusItemClassName}
          strongClassName={ui.strongClassName}
          description="The tracking script that you&#39;ve installed is outdated."
          howtoFix={
            <>
              <AppLink className="text-link" to={{ pathname: installPath }} state={{ requestUrl: finalUrl }}>
                Reinstall
              </AppLink>{' '}
              your Tracking Script. Then re-check its installation.
            </>
          }
        />
      )}
      {warnings.find((warning) => warning.name === 'CACHED_VERSION_OF_TRACKING_SCRIPT') && (
        <WarningItem
          header="Cached Version of Tracking Script"
          iconClassName={ui.iconBackgroundClassName}
          iconGlyphClassName={ui.statusIconClassName}
          iconContainerClassName={ui.statusIconContainerClassName}
          itemClassName={ui.statusItemClassName}
          strongClassName={ui.strongClassName}
          description="The Tracking Script version is incorrect, and may be due to a caching issue. Please ensure there are no
        tools caching the Tracking Script. A cached script will mean any changes you perform on Crazy Egg will not
        be reflected in your site."
          howtoFix={
            <>
              Visit this{' '}
              <SupportLink className="text-link" href={SupportLinks.scriptCheck.cacheScript}>
                help article
              </SupportLink>{' '}
              to learn more.
            </>
          }
        />
      )}
      {warnings.find((warning) => warning.name === 'TRACKING_SCRIPT_IN_NON_OPTIMAL_LOCATION') && (
        <WarningItem
          header="Tracking Script In Non-Optimal Location"
          iconClassName={ui.iconBackgroundClassName}
          iconGlyphClassName={ui.statusIconClassName}
          iconContainerClassName={ui.statusIconContainerClassName}
          itemClassName={ui.statusItemClassName}
          strongClassName={ui.strongClassName}
          description="While this won&#39;t necessarily affect tracking, you&#39;ll get better performance if you add the script to
        the &lt;head&gt; section of your site."
          howtoFix={
            <>
              <AppLink className="text-link" to={{ pathname: installPath }} state={{ requestUrl: finalUrl }}>
                Reinstall
              </AppLink>{' '}
              your Tracking Script. Then re-check its installation.
            </>
          }
        />
      )}
      {warnings.find((warning) => warning.name === 'TRACK_BY_NAME_CODE_DETECTED') && (
        <WarningItem
          header="Track by Name Code Detected"
          iconClassName={ui.iconBackgroundClassName}
          iconGlyphClassName={ui.statusIconClassName}
          iconContainerClassName={ui.statusIconContainerClassName}
          itemClassName={ui.statusItemClassName}
          strongClassName={ui.strongClassName}
          description="The Heatmap for this page does not use the Track by Name method, however a Track by Name code is installed
        on this page."
          howtoFix={
            <>
              Remove the Track by Name code from this page.
              <br />
              Visit this{' '}
              <SupportLink className="text-link" href={SupportLinks.scriptCheck.trackByName}>
                help article
              </SupportLink>{' '}
              to learn more.
            </>
          }
        />
      )}
      {isImpersonated && warnings.find((warning) => warning.name === 'NATIVE_FUNCTIONS_OVERRIDEN') && (
        <WarningItem
          header="Some browser native functions have been overridden"
          iconClassName={ui.iconBackgroundClassName}
          iconGlyphClassName={ui.statusIconClassName}
          iconContainerClassName={ui.statusIconContainerClassName}
          itemClassName={ui.statusItemClassName}
          strongClassName={ui.strongClassName}
          description="You have some code that overrides the following native browser objects: Array, URL, JSON, or XMLHttpRequest. This can cause compatibility issues with not just Crazy Egg but other tools you install on your website. We recommend you remove these overrides."
        />
      )}
      {warnings.find((warning) => warning.name === 'PROTOTYPE_JS_DEFINED') && (
        <WarningItem
          header="Prototype.js framework detected"
          iconClassName={ui.iconBackgroundClassName}
          iconGlyphClassName={ui.statusIconClassName}
          iconContainerClassName={ui.statusIconContainerClassName}
          itemClassName={ui.statusItemClassName}
          strongClassName={ui.strongClassName}
          description="You are using Prototype.js which overrides native browser functions. Prototype.js hasn't been updated since 2015 and is no longer necessary for modern browsers. It can cause compatibility issues with not just Crazy Egg but other tools you install on your website. We recommend you remove it."
        />
      )}
      {warnings.find((warning) => warning.name === 'MONTHLY_VISITS_QUOTA_REACHED') && (
        <WarningItem
          header="Monthly Visits Quota Reached"
          iconClassName={ui.iconBackgroundClassName}
          iconGlyphClassName={ui.statusIconClassName}
          iconContainerClassName={ui.statusIconContainerClassName}
          itemClassName={ui.statusItemClassName}
          strongClassName={ui.strongClassName}
          description="You've used up your monthly pageviews quota. Data is no longer being tracked for any running Heatmaps."
          howtoFix={
            <>
              {showEnterpriseModal ? (
                <button type="button" className="text-link" onClick={() => modal.show(<EnterprisePlanModal />)}>
                  Upgrade
                </button>
              ) : (
                <AppLink
                  className="text-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  to={subscriptionPath}
                  onMouseDown={() => trackMetric('upgrade_cta_click', 'scriptcheck_monthly_visits_quota')}
                >
                  Upgrade
                </AppLink>
              )}{' '}
              now to get more pageviews or wait until your quota resets on the first day of the next month.
            </>
          }
        />
      )}
      {warnings.find((warning) => warning.name === 'TOTAL_SNAPSHOT_QUOTA_REACHED') && (
        <WarningItem
          header="Total Heatmap Quota Reached"
          iconClassName={ui.iconBackgroundClassName}
          iconGlyphClassName={ui.statusIconClassName}
          iconContainerClassName={ui.statusIconContainerClassName}
          itemClassName={ui.statusItemClassName}
          strongClassName={ui.strongClassName}
          description="You have reached the total Heatmap limit for your plan. Heatmaps that are over the limit will not track
        data."
          howtoFix={
            <>
              {showEnterpriseModal ? (
                <button type="button" className="text-link" onClick={() => modal.show(<EnterprisePlanModal />)}>
                  Upgrade
                </button>
              ) : (
                <AppLink
                  className="text-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  to={subscriptionPath}
                  onMouseDown={() => trackMetric('upgrade_cta_click', 'scriptcheck_total_snapshots_quota')}
                >
                  Upgrade
                </AppLink>
              )}{' '}
              your plan, or delete old heatmaps.
            </>
          }
        />
      )}
      {warnings.find((warning) => warning.name === 'MONTHLY_RECORDINGS_QUOTA_REACHED') && (
        <WarningItem
          header="Monthly Recordings Quota Reached"
          iconClassName={ui.iconBackgroundClassName}
          iconGlyphClassName={ui.statusIconClassName}
          iconContainerClassName={ui.statusIconContainerClassName}
          itemClassName={ui.statusItemClassName}
          strongClassName={ui.strongClassName}
          description="You have used up your monthly Recordings quota, so no new visitor sessions will be recorded."
          howtoFix={
            <>
              {showEnterpriseModal ? (
                <button type="button" className="text-link" onClick={() => modal.show(<EnterprisePlanModal />)}>
                  Upgrade
                </button>
              ) : (
                <AppLink
                  className="text-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  to={subscriptionPath}
                  onMouseDown={() => trackMetric('upgrade_cta_click', 'scriptcheck_monthly_recordings_quota')}
                >
                  Upgrade
                </AppLink>
              )}{' '}
              now to get more Recordings or wait until your quota resets on the first day of the next month.
            </>
          }
        />
      )}
    </>
  );
}
